import { forwardRef, useMemo, useState, type ButtonHTMLAttributes } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useCreateProjectNote,
  useProjects,
  useWorkspace,
  useWorkspaceNotes,
} from '@fluxter/shared';
import { Plus } from 'lucide-react';
import { NoteCard, type NoteRecord } from '@/components/NoteCard';
import { NoteRow } from '@/components/NoteRow';
import { NotesGroupHeader } from '@/components/NotesGroupHeader';
import { NotesSubBar, type NotesLayout } from '@/components/NotesSubBar';
import { NotePreviewPanel } from '@/components/NotePreviewPanel';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SkeletonList } from '@/components/Skeletons';
import { groupNotes, type GroupMode } from '@/lib/notes-grouping';
import { useLocalPref } from '@/lib/use-local-pref';
import { cn } from '@/lib/utils';

/* ─ API shape adapters ───────────────────────────────────────────── */

interface NoteAttrs {
  title?: string;
  body?: string;
  project_slug?: string | null;
  project_name?: string | null;
  task_id?: string | null;
  task_title?: string | null;
  created_by?: string | null;
  creator_name?: string | null;
  creator_email?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface NoteListItem {
  id: string;
  attributes?: NoteAttrs;
}

interface NoteListPayload {
  data?: NoteListItem[];
}

interface ProjectListItem {
  id: string;
  attributes?: { name?: string; slug?: string };
}

type ParentFilter = 'all' | 'projects' | 'tasks';

const FILTER_TABS: ReadonlyArray<{ id: ParentFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'projects', label: 'On projects' },
  { id: 'tasks', label: 'On tasks' },
];

/**
 * Map an API note row → the `NoteRecord` shape consumed by NoteCard /
 * NoteRow / NotePreviewPanel. Returns `null` for malformed rows
 * (defensive — every workspace note should have a parent, but the
 * type system permits both `project_id` and `task_id` to be null).
 */
const toNoteRecord = (row: NoteListItem): NoteRecord | null => {
  const a = row.attributes ?? {};
  const updatedIso = a.updated_at ?? a.created_at ?? null;
  const updated = updatedIso ? new Date(updatedIso).getTime() : Date.now();
  const recencyMinutes = Math.max(0, (Date.now() - updated) / 60_000);
  const edited = Boolean(
    a.updated_at && a.created_at && a.updated_at !== a.created_at,
  );

  if (a.task_id) {
    return {
      id: row.id,
      title: a.title ?? '',
      body: a.body ?? '',
      parent: {
        kind: 'task',
        taskId: a.task_id,
        taskTitle: a.task_title ?? 'Task',
      },
      // Task's project isn't exposed by the API yet — this falls
      // through to "(no project)" in the project group, and the rail
      // color comes from the task id hash so each task's notes stay
      // visually distinct.
      projectSlug: null,
      projectName: null,
      railSeed: a.task_id,
      authorId: a.created_by ?? null,
      authorName: a.creator_name ?? null,
      authorEmail: a.creator_email ?? null,
      recencyMinutes,
      edited,
    };
  }
  if (a.project_slug && a.project_name) {
    return {
      id: row.id,
      title: a.title ?? '',
      body: a.body ?? '',
      parent: {
        kind: 'project',
        projectSlug: a.project_slug,
        projectName: a.project_name,
      },
      projectSlug: a.project_slug,
      projectName: a.project_name,
      railSeed: a.project_slug,
      authorId: a.created_by ?? null,
      authorName: a.creator_name ?? null,
      authorEmail: a.creator_email ?? null,
      recencyMinutes,
      edited,
    };
  }
  return null;
};

const isLayout = (raw: unknown): raw is NotesLayout =>
  raw === 'cards' || raw === 'rows' || raw === 'pin';
const isGroupMode = (raw: unknown): raw is GroupMode =>
  raw === 'recency' || raw === 'project' || raw === 'author';

export const WorkspaceNotesPage = () => {
  const { workspaceSlug } = useParams({ from: '/w/$workspaceSlug/notes' });
  const workspace = useWorkspace(workspaceSlug);
  const notes = useWorkspaceNotes(workspaceSlug);
  const projects = useProjects(workspaceSlug);

  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;

  const [filter, setFilter] = useState<ParentFilter>('all');
  const [search, setSearch] = useState('');
  const [openNote, setOpenNote] = useState<NoteRecord | null>(null);
  // Per-page persisted layout + group-by (Q3 + Q4). Workspace +
  // project pages each get their own keys so prefs don't bleed.
  const [layout, setLayout] = useLocalPref<NotesLayout>(
    `notes-layout:${workspaceSlug}:workspace`,
    'cards',
    isLayout,
  );
  const [groupMode, setGroupMode] = useLocalPref<GroupMode>(
    `notes-group:${workspaceSlug}:workspace`,
    'recency',
    isGroupMode,
  );

  /* ─ Adapt + filter + sort + group ────────────────────────────── */
  const allRecords = useMemo<NoteRecord[]>(() => {
    const rows = (notes.data as NoteListPayload | undefined)?.data ?? [];
    return rows.map(toNoteRecord).filter((r): r is NoteRecord => r !== null);
  }, [notes.data]);

  // Tab counts derived from the unfiltered set so they don't shift
  // as the search narrows the visible list (Q9).
  const tabCounts = useMemo(
    () => ({
      all: allRecords.length,
      projects: allRecords.filter((n) => n.parent.kind === 'project').length,
      tasks: allRecords.filter((n) => n.parent.kind === 'task').length,
    }),
    [allRecords],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return allRecords.filter((n) => {
      if (filter === 'projects' && n.parent.kind !== 'project') return false;
      if (filter === 'tasks' && n.parent.kind !== 'task') return false;
      if (!needle) return true;
      const parentLabel =
        n.parent.kind === 'task' ? n.parent.taskTitle : n.parent.projectName;
      const haystack =
        `${n.title} ${n.body} ${parentLabel} ${n.authorName ?? ''} ${n.authorEmail ?? ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [allRecords, filter, search]);

  // Sort: newest first within each bucket. Pinning is deferred (Q5b)
  // so there's no Pinned bucket to elevate.
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => a.recencyMinutes - b.recencyMinutes),
    [filtered],
  );

  const groups = useMemo(() => groupNotes(sorted, groupMode), [sorted, groupMode]);

  /* ─ Topbar slots ─────────────────────────────────────────────── */

  const projectListData =
    (projects.data as { data?: ProjectListItem[] } | undefined)?.data ?? [];

  const topBarLeft = useMemo(
    () => (
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1.5 text-[13px]"
      >
        <span className="text-foreground-muted">{workspaceName ?? 'Workspace'}</span>
        <span aria-hidden="true" className="text-foreground-faint">
          /
        </span>
        <span className="text-foreground font-medium" aria-current="page">
          Notes
        </span>
      </nav>
    ),
    [workspaceName],
  );

  const topBarCta = useMemo(
    () => (
      <NewNotePicker
        workspaceSlug={workspaceSlug}
        projects={projectListData}
        trigger={
          <button
            type="button"
            className={cn(
              'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-lg border-0 px-2.5 text-[12.5px] font-medium text-white transition-shadow duration-100',
              'bg-[var(--primary)]',
              'shadow-[0_2px_8px_oklch(0.3_0.06_245/0.3)] hover:shadow-[0_3px_10px_oklch(0.3_0.06_245/0.4)]',
              'dark:shadow-[0_2px_12px_oklch(0.55_0.12_245/0.4)]',
            )}
          >
            <Plus className="size-3.5" aria-hidden="true" />
            New note
          </button>
        }
      />
    ),
    [workspaceSlug, projectListData],
  );

  useSetTopBarSlots({ left: topBarLeft, cta: topBarCta });

  return (
    <div className="relative flex flex-col gap-4">
      {/* Ambient glow — same vocabulary as the board / calendar. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 blur-[20px]"
        style={{
          background:
            'radial-gradient(50% 40% at 90% 0%, var(--brand-glow-1), transparent 65%),' +
            'radial-gradient(40% 50% at 5% 20%, var(--brand-glow-2), transparent 60%)',
        }}
      />

      <header className="relative z-[1]">
        <h1 className="text-foreground text-[26px] font-semibold tracking-[-0.025em]">
          Notes
        </h1>
        <p className="text-foreground-faint mt-1 max-w-[720px] text-[12.5px] leading-[1.5]">
          Every note across your workspace, newest first. Pin a note to a project from
          that project's Notes tab; pin to a task from the task's detail page.
        </p>
      </header>

      <div className="relative z-[2]">
        <NotesSubBar
          tabs={FILTER_TABS.map((t) => ({
            id: t.id,
            label: t.label,
            count: tabCounts[t.id],
          }))}
          activeTab={filter}
          onTabChange={(id) => setFilter(id as ParentFilter)}
          groupMode={groupMode}
          groupOptions={['recency', 'project', 'author']}
          onGroupChange={setGroupMode}
          layout={layout}
          onLayoutChange={setLayout}
          search={search}
          onSearchChange={setSearch}
        />
      </div>

      <div className="relative z-[1]">
        {notes.isLoading ? (
          <SkeletonList rows={5} withAvatar />
        ) : allRecords.length === 0 ? (
          <EmptyState
            title="No notes yet in this workspace."
            sub="Pin a note from a project's Notes tab or from any task's detail page."
          />
        ) : sorted.length === 0 ? (
          <EmptyState
            title="No notes match the current filter."
            sub="Try clearing the search or switching tabs."
          />
        ) : (
          <div className="flex flex-col gap-[22px]">
            {groups.map((group) => (
              <section key={group.key}>
                <NotesGroupHeader group={group} />
                <NotesLayoutWrapper layout={layout}>
                  {group.items.map((note) =>
                    layout === 'rows' ? (
                      <NoteRow
                        key={note.id}
                        note={note}
                        search={search}
                        onOpen={setOpenNote}
                      />
                    ) : (
                      <NoteCard
                        key={note.id}
                        note={note}
                        search={search}
                        onOpen={setOpenNote}
                      />
                    ),
                  )}
                  {layout === 'cards' && group === groups[groups.length - 1] ? (
                    <NewNotePicker
                      workspaceSlug={workspaceSlug}
                      projects={projectListData}
                      trigger={<NewNoteCardTrigger />}
                    />
                  ) : null}
                </NotesLayoutWrapper>
              </section>
            ))}
          </div>
        )}
      </div>

      <NotePreviewPanel
        workspaceSlug={workspaceSlug}
        note={openNote}
        onClose={() => setOpenNote(null)}
      />
    </div>
  );
};

/* ─ Layout wrapper picks the container element per layout mode ─── */

const NotesLayoutWrapper = ({
  layout,
  children,
}: {
  layout: NotesLayout;
  children: React.ReactNode;
}) => {
  if (layout === 'rows') {
    return (
      <div
        className={cn(
          'flex flex-col overflow-hidden rounded-xl border backdrop-blur-md',
          'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] shadow-[var(--glass-card-shadow)]',
        )}
      >
        {children}
      </div>
    );
  }
  if (layout === 'pin') {
    // CSS multi-column masonry per the prototype's `.v1-notes-pin`.
    // Each child needs `break-inside: avoid` to stay intact.
    return (
      <div className="[column-gap:12px] [columns:3_280px] [&>*]:mb-3 [&>*]:[break-inside:avoid]">
        {children}
      </div>
    );
  }
  return (
    <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
      {children}
    </div>
  );
};

/* ─ NewNoteCard trigger that forwards Radix asChild props ───────── */

/**
 * Same forwardRef pattern as the old `InlineAddNoteRow` — Radix's
 * `PopoverTrigger asChild` clones its child to inject `onClick` /
 * `ref` / aria attrs. A function component that ignores its props
 * would swallow those, breaking the popover trigger silently.
 */
const NewNoteCardTrigger = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => (
  <button
    ref={ref}
    type="button"
    {...props}
    className={cn(
      'flex min-h-[132px] flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed bg-transparent p-4 text-[12px] transition-all duration-100',
      'border-[var(--glass-card-border)] text-foreground-muted',
      'hover:text-foreground hover:border-foreground-faint hover:bg-[var(--glass-card-bg)]',
      '[&:hover_svg]:text-[var(--brand-accent)]',
    )}
  >
    <Plus className="text-foreground-faint size-4 transition-colors duration-100" />
    Add note
  </button>
));
NewNoteCardTrigger.displayName = 'NewNoteCardTrigger';

/* ─ Empty state ───────────────────────────────────────────────── */

const EmptyState = ({ title, sub }: { title: string; sub: string }) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed p-20 text-center backdrop-blur-md',
      'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
    )}
  >
    <span
      className={cn(
        'inline-flex size-12 items-center justify-center rounded-xl border',
        'border-border bg-secondary text-foreground-faint',
      )}
    >
      <Plus className="size-5" aria-hidden="true" />
    </span>
    <p className="text-foreground m-0 text-[14px] font-medium">{title}</p>
    <p className="text-foreground-faint m-0 max-w-[320px] text-[12px]">{sub}</p>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
 *  New-note CTA + project picker.
 *  Workspace notes route is workspace-scoped, but a note belongs to
 *  EITHER a project or a task. The user must pick a project at create
 *  time; the popover lists every project in the workspace and creates
 *  the note inline (POSTs `Untitled` body, navigates to detail).
 *
 *  Reused by both the topbar CTA and the inline NewNoteCardTrigger
 *  at the end of the cards grid.
 * ─────────────────────────────────────────────────────────────── */

interface NewNotePickerProps {
  workspaceSlug: string;
  projects: ProjectListItem[];
  trigger: React.ReactNode;
}

const NewNotePicker = ({ workspaceSlug, projects, trigger }: NewNotePickerProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className={cn(
          'bg-background text-foreground min-w-[240px] rounded-[10px] border p-1.5',
          'border-border shadow-[0_12px_32px_oklch(0_0_0/0.18)] ring-0',
        )}
      >
        <div className="text-foreground-faint px-2 pt-1.5 pb-1 text-[10.5px] font-semibold tracking-[0.06em] uppercase">
          Pick a project
        </div>
        {projects.length === 0 ? (
          <p className="text-foreground-faint px-2 py-2 text-[12px] italic">
            No projects in this workspace yet.
          </p>
        ) : (
          projects.map((p) => (
            <NewNoteProjectOption
              key={p.id}
              workspaceSlug={workspaceSlug}
              projectSlug={p.attributes?.slug ?? ''}
              projectName={p.attributes?.name ?? 'Untitled'}
              onClose={() => setOpen(false)}
            />
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};

const NewNoteProjectOption = ({
  workspaceSlug,
  projectSlug,
  projectName,
  onClose,
}: {
  workspaceSlug: string;
  projectSlug: string;
  projectName: string;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const createNote = useCreateProjectNote(workspaceSlug, projectSlug);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          const result = (await createNote.mutateAsync({
            title: 'Untitled',
            body: '',
          })) as { data?: { id?: string } };
          const id = result?.data?.id;
          onClose();
          if (id) {
            await navigate({
              to: '/w/$workspaceSlug/n/$noteUlid',
              params: { workspaceSlug, noteUlid: id },
            });
          }
        } catch {
          onClose();
        }
      }}
      disabled={createNote.isPending || !projectSlug}
      className="text-foreground hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] disabled:opacity-50"
    >
      <span className="truncate">{projectName}</span>
    </button>
  );
};
